import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { AdjustmentView, StoreView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  Section,
  buttonLinkProps,
} from '@wodi/ui';
import { useEffect, useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import {
  adjustmentsQuery,
  countsQuery,
  decideAdjustment,
  expiringQuery,
  reorderQuery,
  startCount,
  stockOnHandQuery,
  storesQuery,
} from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatIsoDate } from '../patients/format.ts';
import { errorText, newRequestKey, rememberedStoreId, rememberStore } from '../stock/common.tsx';
import {
  expiryWords,
  formatQuantity,
  REASON_LABELS,
  REORDER_LABELS,
  STATUS_LABELS,
  STATUS_TAGS,
} from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores');

/**
 * Stores (docs/plans/phase1-stock.md §5): what each store holds, what is expiring or
 * running low with the numbers behind it, and what is waiting for approval. The store in
 * the address is an internal id; nothing here is patient data.
 */
export function StoresPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { storeId: chosen } = route.useSearch();
  const navigate = useNavigate();
  const stores = useQuery(storesQuery);

  const fallback =
    stores.data?.find((s) => s.id === rememberedStoreId())?.id ?? stores.data?.[0]?.id;
  const storeId = chosen ?? fallback;
  useEffect(() => {
    if (storeId !== undefined) rememberStore(storeId);
  }, [storeId]);

  if (session === null || !can(session, 'stock.read')) {
    return (
      <>
        <PageHeader title="Stores" />
        <Notice tone="info">
          You don't have permission to see stock. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  if (stores.isPending) return <LoadingBlock label="Loading stores" rows={4} />;
  if (stores.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void stores.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(stores.error, "Stores couldn't be loaded. Try again.")}
      </Notice>
    );
  const store = stores.data.find((s) => s.id === storeId);
  if (store === undefined)
    return (
      <>
        <PageHeader title="Stores" />
        <EmptyState>
          {can(session, 'stock.manage')
            ? 'No stores at this facility yet. Add one in Settings, then receive goods or record opening balances.'
            : 'No stores at this facility yet. Ask an administrator to set them up.'}
        </EmptyState>
      </>
    );

  return (
    <div className={styles.page}>
      <PageHeader title={store.name} />
      <div className={styles.toolbar}>
        {stores.data.length > 1 ? (
          <Select
            label="Store"
            options={stores.data.map((s) => ({ id: s.id, label: s.name }))}
            value={store.id}
            onChange={(id) => {
              void navigate({ to: '/stores', search: { storeId: String(id) } });
            }}
          />
        ) : null}
        <StoreActions store={store} />
      </div>
      <Approvals store={store} />
      <OnHand store={store} />
      <Expiring store={store} />
      <LowStock store={store} />
      <Counts store={store} />
    </div>
  );
}

function StoreActions({ store }: { store: StoreView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const start = useMutation({
    mutationFn: () => startCount(store.id, newRequestKey()),
    onSuccess: (count) => {
      void navigate({
        to: '/stores/$storeId/counts/$countId',
        params: { storeId: store.id, countId: count.id },
      });
    },
  });
  if (session === null) return null;
  return (
    <div className={styles.actions}>
      {can(session, 'stock.receive') ? (
        <Link to="/stores/$storeId/receive" params={{ storeId: store.id }} {...buttonLinkProps()}>
          Receive goods
        </Link>
      ) : null}
      {can(session, 'stock.transfer') ? (
        <Link to="/stores/$storeId/transfer" params={{ storeId: store.id }} {...buttonLinkProps()}>
          Transfer stock
        </Link>
      ) : null}
      <Link to="/stores/$storeId/receipts" params={{ storeId: store.id }}>
        Deliveries received
      </Link>
      <Link to="/stores/$storeId/requisitions" params={{ storeId: store.id }}>
        Store requisitions
      </Link>
      {can(session, 'stock.adjust.request') ? (
        <Link to="/stores/$storeId/adjust" params={{ storeId: store.id }} {...buttonLinkProps()}>
          Request adjustment
        </Link>
      ) : null}
      {can(session, 'stock.adjust.request') && can(session, 'imports.read') ? (
        <Link
          to="/settings/imports"
          search={{ kind: 'opening_stock', storeId: store.id }}
          {...buttonLinkProps()}
        >
          Import opening balances
        </Link>
      ) : null}
      {can(session, 'stock.count') ? (
        <Button
          isDisabled={!online}
          isPending={start.isPending}
          pendingLabel="Starting…"
          onPress={() => {
            start.mutate();
          }}
        >
          Start count
        </Button>
      ) : null}
      {start.error === null ? null : (
        <Notice tone="error">{errorText(start.error, "The count didn't start. Try again.")}</Notice>
      )}
    </div>
  );
}

function OnHand({ store }: { store: StoreView }) {
  const stock = useQuery(stockOnHandQuery(store.id));
  return (
    <Section title="Stock on hand">
      {stock.isPending ? <LoadingBlock label="Loading stock" rows={4} /> : null}
      {stock.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void stock.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(stock.error, "Stock couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {stock.isSuccess ? (
        <div className={styles.onHand}>
          <TableScroll>
            <Table flat aria-label={`Stock on hand in ${store.name}`}>
              <TableHeader>
                <Column isRowHeader>Item</Column>
                <Column numeric>Usable</Column>
                <Column numeric>Expired</Column>
                <Column>Next expiry</Column>
                <Column numeric>Reorder level</Column>
                <Column>Status</Column>
              </TableHeader>
              <TableBody
                items={stock.data.items.map((i) => ({ ...i, id: i.itemId }))}
                renderEmptyState={() =>
                  'No stock in this store yet. Receive goods or record opening balances to start.'
                }
              >
                {(row) => (
                  <Row id={row.itemId}>
                    <Cell>
                      <Link
                        to="/stores/$storeId/items/$itemId"
                        params={{ storeId: store.id, itemId: row.itemId }}
                      >
                        {row.name}
                      </Link>
                    </Cell>
                    <Cell numeric>{formatQuantity(row.usable, row.baseUnitDisplay)}</Cell>
                    <Cell numeric>
                      {row.expired === 0 ? '' : formatQuantity(row.expired, row.baseUnitDisplay)}
                    </Cell>
                    <Cell>
                      {row.earliestUsableExpiry === null
                        ? ''
                        : formatIsoDate(row.earliestUsableExpiry)}
                    </Cell>
                    <Cell numeric>{row.reorderLevel === null ? '' : String(row.reorderLevel)}</Cell>
                    <Cell>
                      <Tag
                        state={
                          row.reorderStatus === 'ok' || row.reorderStatus === 'not_set'
                            ? 'done'
                            : 'attention'
                        }
                      >
                        {REORDER_LABELS[row.reorderStatus]}
                      </Tag>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        </div>
      ) : null}
    </Section>
  );
}

function Expiring({ store }: { store: StoreView }) {
  const rows = useQuery(expiringQuery(store.id));
  return (
    <Section title="Expired and expiring within 90 days">
      {rows.isPending ? <LoadingBlock label="Loading expiring stock" rows={2} /> : null}
      {rows.isError ? (
        <Notice tone="error">{errorText(rows.error, "Expiring stock couldn't be loaded.")}</Notice>
      ) : null}
      {rows.isSuccess ? (
        <TableScroll>
          <Table flat aria-label="Expired and expiring batches" dense>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column>Batch</Column>
              <Column>Expiry</Column>
              <Column>When</Column>
              <Column numeric>Quantity</Column>
            </TableHeader>
            <TableBody
              items={rows.data.map((r) => ({ ...r, id: r.batchId }))}
              renderEmptyState={() => 'Nothing expired or expiring in the next 90 days.'}
            >
              {(row) => (
                <Row id={row.batchId}>
                  <Cell>{row.name}</Cell>
                  <Cell>
                    <span className="wodi-id">{row.batchNumber}</span>
                  </Cell>
                  <Cell>{formatIsoDate(row.expiryDate)}</Cell>
                  <Cell>
                    <Tag state={row.status === 'expired' ? 'failed' : 'attention'}>
                      {expiryWords(row.daysToExpiry)}
                    </Tag>
                  </Cell>
                  <Cell numeric>{formatQuantity(row.quantity, row.baseUnitDisplay)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}

function LowStock({ store }: { store: StoreView }) {
  const rows = useQuery(reorderQuery(store.id));
  return (
    <Section title="Low stock">
      {rows.isPending ? <LoadingBlock label="Loading low stock" rows={2} /> : null}
      {rows.isError ? (
        <Notice tone="error">{errorText(rows.error, "Low stock couldn't be loaded.")}</Notice>
      ) : null}
      {rows.isSuccess ? (
        <TableScroll>
          <Table flat aria-label="Items at or below their reorder level" dense>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column numeric>Usable</Column>
              <Column numeric>Reorder level</Column>
              <Column>Status</Column>
            </TableHeader>
            <TableBody
              items={rows.data.map((r) => ({ ...r, id: r.itemId }))}
              renderEmptyState={() => 'Nothing is at or below its reorder level.'}
            >
              {(row) => (
                <Row id={row.itemId}>
                  <Cell>{row.name}</Cell>
                  <Cell numeric>{formatQuantity(row.usable, row.baseUnitDisplay)}</Cell>
                  <Cell numeric>
                    {row.reorderLevel === null ? 'Not set' : String(row.reorderLevel)}
                  </Cell>
                  <Cell>
                    <Tag state="attention">{REORDER_LABELS[row.reorderStatus]}</Tag>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}

function Approvals({ store }: { store: StoreView }) {
  const adjustments = useQuery(adjustmentsQuery(store.id));
  const waiting = (adjustments.data ?? []).filter((a) => a.status === 'requested');
  if (adjustments.isError)
    return (
      <Notice tone="error">
        {errorText(adjustments.error, "Adjustments waiting for approval couldn't be loaded.")}
      </Notice>
    );
  if (waiting.length === 0) return null;
  return (
    <Section title="Waiting for approval">
      {waiting.map((a) => (
        <AdjustmentCard key={a.id} adjustment={a} storeId={store.id} />
      ))}
    </Section>
  );
}

function AdjustmentCard({ adjustment, storeId }: { adjustment: AdjustmentView; storeId: string }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<'approve' | 'reject' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');
  const decide = useMutation({
    mutationFn: (d: 'approve' | 'reject' | 'cancel') =>
      decideAdjustment(adjustment.id, d, reason.trim()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['stock', storeId] });
      toasts.show({
        title:
          result.status === 'approved'
            ? 'Adjustment approved'
            : result.status === 'rejected'
              ? 'Adjustment rejected'
              : 'Adjustment cancelled',
        description: result.number,
      });
      setDeciding(null);
      setReason('');
    },
  });
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mine = session?.user.id === adjustment.requestedBy.id;
  return (
    <div className={styles.line}>
      <div className={styles.lineHeading}>
        <span>
          <span className="wodi-id">{adjustment.number}</span>{' '}
          {adjustment.kind === 'opening_balance'
            ? 'Opening balance'
            : REASON_LABELS[adjustment.reasonCode]}
        </span>
        <Tag state={STATUS_TAGS[adjustment.status]}>{STATUS_LABELS[adjustment.status]}</Tag>
      </div>
      <p className={styles.meta}>
        Requested by {adjustment.requestedBy.name}
        {adjustment.note === null ? '' : `: ${adjustment.note}`}
      </p>
      <ul>
        {adjustment.lines.map((l) => (
          <li key={l.lineNo} className={styles.numbers}>
            {l.itemName}, batch <span className="wodi-id">{l.batchNumber}</span>:{' '}
            {l.quantity > 0 ? `add ${l.quantity}` : `remove ${-l.quantity}`}
          </li>
        ))}
      </ul>
      <div className={styles.actions}>
        {adjustment.canDecide ? (
          <>
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setDeciding('approve');
              }}
            >
              Approve
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setDeciding('reject');
              }}
            >
              Reject
            </Button>
          </>
        ) : mine ? (
          <>
            <span className={styles.meta}>
              Another person with approval rights must approve this.
            </span>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setDeciding('cancel');
              }}
            >
              Cancel request
            </Button>
          </>
        ) : null}
      </div>
      {deciding === null ? null : (
        <Dialog
          title={
            deciding === 'approve'
              ? `Approve ${adjustment.number}`
              : deciding === 'reject'
                ? `Reject ${adjustment.number}`
                : `Cancel ${adjustment.number}`
          }
          isOpen
          onOpenChange={(open) => {
            if (!open) setDeciding(null);
          }}
        >
          <TextArea
            label="Reason"
            description="At least five characters. It is kept with the decision."
            value={reason}
            onChange={setReason}
            autoFocus
          />
          {decide.error === null ? null : (
            <Notice tone="error">{errorText(decide.error, "That wasn't saved. Try again.")}</Notice>
          )}
          <DialogActions>
            <Button
              variant="primary"
              isDisabled={reason.trim().length < 5 || !online}
              isPending={decide.isPending}
              pendingLabel="Saving…"
              onPress={() => {
                decide.mutate(deciding);
              }}
            >
              {deciding === 'approve'
                ? 'Approve'
                : deciding === 'reject'
                  ? 'Reject'
                  : 'Cancel request'}
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setDeciding(null);
              }}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

function Counts({ store }: { store: StoreView }) {
  const counts = useQuery(countsQuery(store.id));
  const open = (counts.data ?? []).filter(
    (c) => c.status === 'in_progress' || c.status === 'submitted',
  );
  if (open.length === 0) return null;
  return (
    <Section title="Counts">
      <ul>
        {open.map((c) => (
          <li key={c.id}>
            <Link
              to="/stores/$storeId/counts/$countId"
              params={{ storeId: store.id, countId: c.id }}
            >
              <span className="wodi-id">{c.number}</span>
            </Link>{' '}
            {STATUS_LABELS[c.status]}
            {c.canDecide ? ', you can approve it' : ''}
          </li>
        ))}
      </ul>
    </Section>
  );
}
